import React, { memo, useRef } from 'react'

import { Close, Drag, Icon } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, node, string } from 'prop-types'
import { createPortal } from 'react-dom'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import ResizeHandles from './Components/ResizeHandles/ResizeHandles'
import useDraggable from './useDraggable'
import useFitOnOpen from './useFitOnOpen'
import useRememberedGeometry from './useRememberedGeometry'
import useResizeHandles from './useResizeHandles'

// Initial width, used to place the window before it mounts; keep in sync with
// .detached-window in _detached_window.scss. Users can resize it afterwards.
const WINDOW_WIDTH = 440

function DetachedWindow({ title, onClose: handleClose, children }) {
  const t = useTranslation()
  const windowRef = useRef(null)
  const bodyRef = useRef(null)
  const {
    position,
    setPosition,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleKeyDown,
  } = useDraggable(windowRef, WINDOW_WIDTH)
  const resizeHandlers = useResizeHandles(windowRef, position, setPosition)
  const isRestored = useRememberedGeometry(windowRef, setPosition)
  useFitOnOpen(windowRef, bodyRef, setPosition, !isRestored)

  return createPortal(
    <section
      ref={windowRef}
      className="detached-window"
      style={{ left: position.x, top: position.y }}
      role="dialog"
      aria-label={title}
    >
      <ResizeHandles handlers={resizeHandlers} />
      <div
        className="detached-window__header"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <span
          className="detached-window__handle"
          role="button"
          tabIndex={0}
          aria-label={t('viewerSidebar.enhance.variations.window.move')}
          onKeyDown={handleKeyDown}
        >
          <Icon svgComponent={Drag} color="dark" size={iconSize} notSpaced />
        </span>
        <span className="detached-window__title cgt-heading-50">{title}</span>
        <button
          type="button"
          className="detached-window__close"
          aria-label={t('viewerSidebar.enhance.variations.window.close')}
          onClick={handleClose}
        >
          <Icon svgComponent={Close} color="dark" size={iconSize} notSpaced />
        </button>
      </div>
      <div ref={bodyRef} className="detached-window__body">
        {children}
      </div>
    </section>,
    document.body,
  )
}

DetachedWindow.propTypes = {
  title: string.isRequired,
  onClose: func.isRequired,
  children: node.isRequired,
}

export default memo(DetachedWindow)
